import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, Stack, Grid, Typography } from '@mui/material';
import {
  Button,
  DropdownSelect,
  type DropDownSelectOption,
  TextField,
} from '../';
import { PAGE_TEXT } from '../../pages/Assessment/lang';

export type OptionGroups = {
  title: string;
  options?: DropDownSelectOption[];
  type: 'text' | 'select'; // to  be expanded
  displayLabel?: boolean;

  // for query use
  fieldName: string;
  fieldType?: string;
  isFuzzy?: boolean;
};

export type TableFilterProps = {
  optionGroups: OptionGroups[];
  onApply: (selectedOptionGroupsArr: string[][]) => void;
  appliedSelection: string[][];
};

export const TableFilter: React.FunctionComponent<TableFilterProps> = (
  props: TableFilterProps,
) => {
  const { optionGroups, onApply, appliedSelection } = props;

  const [selectedOptionGroupsArr, setSelectedOptionGroupsArr] =
    useState<string[][]>(appliedSelection);

  const [clearKey, setClearKey] = useState(0);

  const handleApply = (): void => {
    onApply(selectedOptionGroupsArr);
  };

  const handleClear = (): void => {
    const emptySelection: string[][] = Array.from(
      { length: optionGroups.length },
      () => [],
    );
    setClearKey(prevState => prevState + 1);
    setSelectedOptionGroupsArr(emptySelection);
    onApply(emptySelection);
  };

  const handleDropdownChange = (
    selectedValue: string[],
    index?: number,
  ): void => {
    if (index !== undefined) {
      const selectedValuesArr: string[] = Object.values(selectedValue);
      setSelectedOptionGroupsArr(prevState => {
        const newState = [...prevState];
        newState[index] = [...selectedValuesArr];
        return newState;
      });
    }
  };

  const handleStringChange = (value: string, index: number): void => {
    setSelectedOptionGroupsArr(prevState => {
      const newState = [...prevState];
      newState[index] = [value];
      return newState;
    });
  };

  const renderRow = (index: number): React.ReactNode => {
    if (index % 2) return <></>;
    const {
      title = '',
      options = [],
      type,
      displayLabel,
    } = optionGroups[index];

    return (
      <Box key={title + index} sx={{ display: 'flex', width: '100%' }}>
        <Grid container rowSpacing={2} className="w-full">
          {/* Left */}
          <Grid size={{ xs: 12, md: 6 }}>
            {renderCell(title, type, index, displayLabel, options)}
          </Grid>

          {/* Right */}
          <Grid size={{ xs: 12, md: 6 }}>
            {index < optionGroups.length - 1 &&
              renderCell(
                optionGroups?.[index + 1]?.title ?? '',
                optionGroups[index + 1]?.type,
                index + 1,
                optionGroups[index + 1]?.displayLabel,
                optionGroups[index + 1]?.options ?? [],
              )}
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderCell = (
    title: string,
    type: 'text' | 'select',
    index: number,
    displayLabel?: boolean,
    options?: DropDownSelectOption[],
  ): React.ReactNode => {
    return (
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ width: '100%' }}
        key={title + index + 'item'}
      >
        <Grid key={title + index + 'title'} size={4}>
          <Typography variant="body2">{title}</Typography>
        </Grid>
        <Grid key={title + index + 'input'} size={8}>
          {type === 'select' ? (
            <DropdownSelect
              // force rerender on clear
              fullWidth
              key={`${index}-${clearKey}`}
              options={options ?? []}
              index={index}
              value={selectedOptionGroupsArr?.[index]}
              onChange={handleDropdownChange}
              displayLabel={displayLabel as boolean}
            />
          ) : (
            <TextField
              fullWidth
              key={`${index}-${clearKey}`}
              sx={{
                margin: '8px',
                width: '90%',
                maxWidth: '340px',
              }}
              size="small"
              value={selectedOptionGroupsArr?.[index]?.[0] ?? ''}
              onChange={e => {
                handleStringChange(e.target.value, index);
              }}
            />
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Container>
      <Stack className="ml-4">
        <div className="w-full">
          {optionGroups.map((group, index) => {
            return <span key={`row-${index}`}> {renderRow(index)}</span>;
          })}
        </div>

        <Box className="buttons-area">
          <Button
            variant="contained"
            type="submit"
            onClick={handleApply}
            sx={{ textTransform: 'none' }}
          >
            {PAGE_TEXT.APPLY}
          </Button>
          <Button
            variant="text"
            onClick={handleClear}
            sx={{ textTransform: 'none' }}
          >
            {PAGE_TEXT.CLEAR}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    button {
      margin: 0 1rem;
    }
  }
`;
